import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { usePerm } from "../context/PermContext";
import { Field, Loading, PageHeader, Panel } from "../components/common";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Button } from "../components/ui/button";
import { CertManager } from "../components/CertManager";
import { AvatarEditor } from "../components/AvatarEditor";

const FIELDS = [["nome", "Nome *"], ["cognome", "Cognome *"], ["email", "Email di accesso *"], ["data_nascita", "Data di nascita (GG/MM/AAAA)"], ["luogo_nascita", "Luogo di nascita"], ["codice_fiscale", "Codice Fiscale"], ["telefono", "Telefono"], ["indirizzo", "Indirizzo di residenza"], ["citta", "Città"], ["ore_settimanali", "Ore settimanali"], ["giorni_settimanali", "Giorni settimanali"]];
const EMPTY = Object.fromEntries(FIELDS.map(([k]) => [k, ""]));

function SetPassword({ id }) {
  const [p, setP] = useState({ a: "", b: "" });
  const save = async () => {
    if (p.a.length < 6) return toast.error("La password deve contenere almeno 6 caratteri");
    if (p.a !== p.b) return toast.error("Le password non coincidono");
    try { await api(`/admin/users/${id}/set-password`, { method: "POST", body: { new_password: p.a } }); toast.success("Password aggiornata"); setP({ a: "", b: "" }); } catch (e) { toast.error(e.message); }
  };
  return (
    <Panel className="space-y-4 mt-6"><h2 className="font-display font-bold text-[var(--navy)]">Imposta nuova password</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Nuova password"><Input type="password" value={p.a} onChange={(e) => setP({ ...p, a: e.target.value })} placeholder="Almeno 6 caratteri" data-testid="user-newpwd-input" /></Field>
        <Field label="Conferma password"><Input type="password" value={p.b} onChange={(e) => setP({ ...p, b: e.target.value })} data-testid="user-newpwd-confirm-input" /></Field>
      </div>
      <Button variant="outline" className="rounded-full" onClick={save} data-testid="user-setpwd-button">Aggiorna password</Button>
    </Panel>
  );
}

export default function UserForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const [f, setF] = useState(id ? null : { ...EMPTY, password: "", legge_104: false, is_active: true, data_visita_medica: "", visita_medica_validita: 1 });
  const [mansioni, setMansioni] = useState([]);
  const { profiles, reload } = usePerm();
  useEffect(() => { reload(); }, [reload]);
  const names = [...new Set([...(profiles || []).map((p) => p.name), ...mansioni])];
  useEffect(() => {
    if (!id) return;
    api("/admin/users").then((d) => { const u = (d.users || []).find((x) => x.id === id); if (u) { setF({ ...EMPTY, ...u }); setMansioni(u.mansioni?.length ? u.mansioni : u.mansione ? [u.mansione] : []); } else setF(false); });
  }, [id]);
  if (f === null) return <Loading />;
  if (f === false) return <p>Utente non trovato.</p>;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggle = (m) => setMansioni((p) => (p.includes(m) ? p.filter((x) => x !== m) : [...p, m]));

  const save = async () => {
    if (!f.nome || !f.cognome || !f.email || !mansioni.length || (!id && !f.password)) return toast.error(id ? "Nome, cognome e almeno una mansione sono obbligatori" : "Compila nome, cognome, email, password e almeno una mansione");
    try {
      if (id) {
        const body = Object.fromEntries(FIELDS.map(([k]) => [k, (f[k] ?? "").toString()]));
        await api(`/admin/users/${id}`, { method: "PUT", body: { ...body, email: body.email.trim(), mansioni, legge_104: !!f.legge_104, data_visita_medica: f.data_visita_medica || "", visita_medica_validita: parseInt(f.visita_medica_validita || 1, 10), is_active: f.is_active !== false } });
        toast.success("Utente aggiornato");
      } else {
        await api("/admin/users", { method: "POST", body: { ...f, mansioni } });
        toast.success("Utente creato");
      }
      nav("/admin/users");
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="max-w-3xl">
      <PageHeader subtitle="Gestione Utenti" title={id ? "Modifica Utente" : "Nuovo Utente"} />
      <Panel className="space-y-6">
        {id && <AvatarEditor uid={id} />}
        <div className="grid sm:grid-cols-2 gap-4">
          {FIELDS.map(([k, l]) => <Field key={k} label={l}><Input value={f[k] ?? ""} onChange={set(k)} data-testid={`user-${k}-input`} /></Field>)}
          {!id && <Field label="Password iniziale *"><Input type="password" value={f.password} onChange={set("password")} data-testid="user-password-input" /></Field>}
          {id && <Field label="Data visita medica"><Input placeholder="GG/MM/AAAA" value={f.data_visita_medica || ""} onChange={set("data_visita_medica")} data-testid="user-medical-date-input" /></Field>}
          {id && <Field label="Validità visita medica (anni)"><Input type="number" min="1" value={f.visita_medica_validita || 1} onChange={set("visita_medica_validita")} data-testid="user-medical-validity-input" /></Field>}
        </div>
        <Field label="Profili *"><div className="flex flex-wrap gap-2 pt-1">{names.map((m) => (
          <button key={m} type="button" onClick={() => toggle(m)} className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${mansioni.includes(m) ? "bg-[var(--navy)] text-white border-[var(--navy)]" : "bg-white hover:border-[var(--teal)]"}`} data-testid={`user-role-${m.replace(/\s/g, "-").toLowerCase()}`}>{m}</button>))}</div></Field>
        <div className="flex flex-wrap gap-8">
          <label className="flex items-center gap-3 text-sm"><Switch checked={!!f.legge_104} onCheckedChange={(v) => setF({ ...f, legge_104: v })} data-testid="user-104-switch" />Agevolazioni Legge 104</label>
          {id && <label className="flex items-center gap-3 text-sm"><Switch checked={f.is_active !== false} onCheckedChange={(v) => setF({ ...f, is_active: v })} data-testid="user-active-switch" />Attivo</label>}
        </div>
        <Button onClick={save} className="rounded-full bg-[var(--teal)] px-8" data-testid="user-save-button">{id ? "Salva modifiche" : "Crea Utente"}</Button>
      </Panel>
      {id && <SetPassword id={id} />}
      {id && <Panel className="mt-6"><CertManager basePath={`/admin/users/${id}/certifications`} /></Panel>}
    </div>
  );
}
